import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiService } from '../../services/api.service';
import { useTenantStore } from '../../stores/tenant.store';
import { usePermissions } from '../../context/PermissionsContext';
import {
  formatMoney,
  formatDateTime,
  registerTypeLabel,
  transferStatusLabel,
  humanizeTreasuryError,
  CTA_PRIMARY,
} from './treasuryUi';
import { TreasuryEmptyState, TreasurySkeleton } from './TreasuryFeedback';

const LINKABLE_TYPES = new Set(['CENTRAL_TREASURY', 'BRANCH_MASTER']);

const TEMPLATE_OPTIONS = [
  { value: 'GENERIC_AR', label: 'Genérico AR' },
  { value: 'GALICIA', label: 'Galicia' },
  { value: 'SANTANDER', label: 'Santander' },
  { value: 'BBVA', label: 'BBVA' },
  { value: 'MACRO', label: 'Macro' },
  { value: 'NACION', label: 'Nación' },
  { value: 'PROVINCIA', label: 'Provincia' },
  { value: 'CREDICOOP', label: 'Credicoop' },
];

function registerLabel(r: {
  type?: string;
  name?: string;
  code?: string;
  branch?: { name?: string; code?: string } | null;
}) {
  const branch = r.branch?.name || r.branch?.code;
  return `${registerTypeLabel(r.type)} · ${r.name || r.code || '—'}${
    branch ? ` · ${branch}` : ''
  }`;
}

function linkedBranches(acc: any): string[] {
  const names = new Set<string>();
  if (acc?.branch?.name) names.add(acc.branch.name);
  for (const link of acc?.cashRegisters ?? []) {
    const b = link.cashRegister?.branch?.name || link.cashRegister?.branch?.code;
    if (b) names.add(b);
  }
  return [...names];
}

/**
 * TX6 — Bancos en Tesorería: editar cuenta + vínculos caja↔banco.
 */
export function TreasuryBanksPage() {
  const legalEntityId = useTenantStore((s) => s.legalEntityId);
  const { can } = usePermissions();
  const canWrite = can('treasury-banks', 'write') || can('cash', 'write') || can('bank-reconciliation', 'write');
  const [searchParams, setSearchParams] = useSearchParams();
  const focusAccountId = searchParams.get('account') || '';
  const showLinkPanel = searchParams.get('link') === '1';
  const showEditPanel = searchParams.get('edit') === '1';

  const [accounts, setAccounts] = useState<any[]>([]);
  const [branches, setBranches] = useState<any[]>([]);
  const [balances, setBalances] = useState<Record<string, number>>({});
  const [linkableRegisters, setLinkableRegisters] = useState<any[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState(focusAccountId);
  const [selectedRegisterIds, setSelectedRegisterIds] = useState<string[]>([]);
  const [movementsAccountId, setMovementsAccountId] = useState<string | null>(null);
  const [cashBankTransfers, setCashBankTransfers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editForm, setEditForm] = useState({
    bankName: '',
    alias: '',
    accountNumber: '',
    cbu: '',
    templateCode: 'GENERIC_AR',
    branchId: '',
    isActive: true,
  });

  const load = async () => {
    if (!legalEntityId) return;
    setLoading(true);
    setError('');
    try {
      const [accs, regs, summary, transfers, br] = await Promise.all([
        apiService.listBankAccounts(),
        apiService.getCashRegisters(undefined, legalEntityId),
        apiService.getTreasurySummary(legalEntityId).catch(() => null),
        apiService.listCashBankTransfers(legalEntityId),
        apiService.getBranches(true).catch(() => []),
      ]);
      const forEntity = (accs ?? []).filter(
        (a: any) => !a.legalEntityId || a.legalEntityId === legalEntityId,
      );
      setAccounts(forEntity);
      setLinkableRegisters((regs ?? []).filter((r: any) => LINKABLE_TYPES.has(r.type)));
      setBranches(
        (br ?? []).filter(
          (b: any) => !b.legalEntityId || b.legalEntityId === legalEntityId,
        ),
      );
      const balMap: Record<string, number> = {};
      for (const b of summary?.banks ?? []) {
        balMap[b.id] = Number(b.balance ?? 0);
      }
      setBalances(balMap);
      setCashBankTransfers(transfers ?? []);
      if (!selectedAccountId && forEntity.length) {
        setSelectedAccountId(forEntity[0].id);
      }
    } catch (e: any) {
      setError(humanizeTreasuryError(e?.response?.data?.message || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legalEntityId]);

  useEffect(() => {
    if (focusAccountId) setSelectedAccountId(focusAccountId);
  }, [focusAccountId]);

  useEffect(() => {
    if (!selectedAccountId) {
      setSelectedRegisterIds([]);
      return;
    }
    const acc = accounts.find((a) => a.id === selectedAccountId);
    setSelectedRegisterIds(
      (acc?.cashRegisters ?? [])
        .map((l: any) => l.cashRegisterId || l.cashRegister?.id)
        .filter(Boolean),
    );
    if (acc) {
      setEditForm({
        bankName: acc.bankName || '',
        alias: acc.alias || '',
        accountNumber: acc.accountNumber || '',
        cbu: acc.cbu || '',
        templateCode: acc.templateCode || 'GENERIC_AR',
        branchId: acc.branchId || '',
        isActive: acc.isActive !== false,
      });
    }
  }, [selectedAccountId, accounts]);

  const selectedAccount = useMemo(
    () => accounts.find((a) => a.id === selectedAccountId) ?? null,
    [accounts, selectedAccountId],
  );

  const accountTransfers = useMemo(() => {
    if (!movementsAccountId) return [];
    return cashBankTransfers.filter((t) => t.bankAccountId === movementsAccountId);
  }, [cashBankTransfers, movementsAccountId]);

  const toggleRegister = (id: string) => {
    setSelectedRegisterIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const saveLinks = async () => {
    if (!selectedAccountId || !canWrite) return;
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      await apiService.setBankAccountCashRegisters(selectedAccountId, selectedRegisterIds);
      setSuccess('Vínculo con Madres/Central actualizado. Ya pueden enviar plata a este banco.');
      await load();
    } catch (e: any) {
      setError(humanizeTreasuryError(e?.response?.data?.message || e.message));
    } finally {
      setBusy(false);
    }
  };

  const saveAccount = async () => {
    if (!selectedAccountId || !canWrite) return;
    if (!editForm.bankName.trim()) {
      setError('El nombre del banco es obligatorio.');
      return;
    }
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      await apiService.updateBankAccount(selectedAccountId, {
        bankName: editForm.bankName.trim(),
        alias: editForm.alias.trim() || null,
        accountNumber: editForm.accountNumber.trim() || null,
        cbu: editForm.cbu.trim() || null,
        templateCode: editForm.templateCode,
        branchId: editForm.branchId.trim() || null,
        isActive: editForm.isActive,
      });
      setSuccess('Datos del banco actualizados.');
      await load();
    } catch (e: any) {
      setError(humanizeTreasuryError(e?.response?.data?.message || e.message));
    } finally {
      setBusy(false);
    }
  };

  const openLinkFor = (accountId: string) => {
    setSelectedAccountId(accountId);
    setSearchParams({ account: accountId, link: '1' });
  };

  const openEditFor = (accountId: string) => {
    setSelectedAccountId(accountId);
    setSearchParams({ account: accountId, edit: '1', link: '1' });
  };

  const linkedSummary = (acc: any) => {
    const links = acc?.cashRegisters ?? [];
    if (!links.length) return 'Sin Madres vinculadas';
    return links
      .map((l: any) => {
        const r = l.cashRegister;
        if (!r) return null;
        return registerLabel(r);
      })
      .filter(Boolean)
      .join(', ');
  };

  const showPanel = (showLinkPanel || showEditPanel || selectedAccount) && canWrite;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Bancos</h2>
        <p className="text-sm text-gray-600 mt-1">
          Editá los datos de la cuenta, mirá a qué sucursales llega (vía Madres) y vinculá una o
          varias Cajas Madre/Central para que puedan enviar plata.
        </p>
      </div>

      {!legalEntityId && (
        <p className="text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-sm">
          Seleccioná una empresa en la barra de contexto.
        </p>
      )}
      {error && (
        <p className="text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 text-sm">{error}</p>
      )}
      {success && (
        <p className="text-green-800 bg-green-50 border border-green-200 rounded-md px-3 py-2 text-sm">
          {success}
        </p>
      )}

      <div className="overflow-x-auto border rounded-lg bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left">
            <tr>
              <th className="px-3 py-2">Banco</th>
              <th className="px-3 py-2">Alias / CBU</th>
              <th className="px-3 py-2">Sucursal ref.</th>
              <th className="px-3 py-2">Sucursales (Madres)</th>
              <th className="px-3 py-2">Saldo</th>
              <th className="px-3 py-2">Estado</th>
              <th className="px-3 py-2">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} className="px-3 py-4">
                  <TreasurySkeleton kind="table" rows={4} className="border-0" />
                </td>
              </tr>
            )}
            {!loading && accounts.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-2">
                  <TreasuryEmptyState
                    title="No hay cuentas bancarias configuradas."
                    description="El alta de cuentas (plantilla de extracto) se hace en Conciliación."
                    action={
                      <Link to="/bank-reconciliation" className={CTA_PRIMARY}>
                        Ir a Conciliación
                      </Link>
                    }
                  />
                </td>
              </tr>
            )}
            {!loading &&
              accounts.map((a) => {
                const bal = balances[a.id];
                const branchesForBank = linkedBranches(a);
                return (
                  <tr
                    key={a.id}
                    id={`bank-${a.id}`}
                    className={`border-t ${selectedAccountId === a.id ? 'bg-slate-50' : ''}`}
                  >
                    <td className="px-3 py-2 font-medium">{a.bankName}</td>
                    <td className="px-3 py-2">
                      {a.alias || a.accountNumber || '—'}
                      {a.cbu ? (
                        <span className="block text-xs text-gray-500">…{String(a.cbu).slice(-8)}</span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2">{a.branch?.name ?? 'Empresa'}</td>
                    <td className="px-3 py-2 max-w-[14rem]">
                      {branchesForBank.length ? (
                        <span className="text-gray-800">{branchesForBank.join(', ')}</span>
                      ) : (
                        <span className="text-amber-700">Sin Madres vinculadas</span>
                      )}
                      <span className="block text-xs text-gray-500 mt-0.5">{linkedSummary(a)}</span>
                    </td>
                    <td className="px-3 py-2 tabular-nums">
                      {bal != null ? formatMoney(bal, a.currency || 'ARS') : '—'}
                    </td>
                    <td className="px-3 py-2">
                      {a.isActive === false ? (
                        <span className="text-red-700">Inactiva</span>
                      ) : (
                        <span className="text-green-700">Activa</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-2">
                        {canWrite && (
                          <>
                            <button
                              type="button"
                              className="text-slate-900 hover:underline font-medium"
                              onClick={() => openEditFor(a.id)}
                            >
                              Editar
                            </button>
                            <button
                              type="button"
                              className="text-slate-700 hover:underline"
                              onClick={() => openLinkFor(a.id)}
                            >
                              Vincular Madres
                            </button>
                            <Link
                              to={`/treasury/move?type=deposit&bank=${encodeURIComponent(a.id)}`}
                              className="text-blue-700 hover:underline"
                            >
                              Depositar
                            </Link>
                            <Link
                              to={`/treasury/move?type=withdrawal&bank=${encodeURIComponent(a.id)}`}
                              className="text-blue-700 hover:underline"
                            >
                              Retirar
                            </Link>
                          </>
                        )}
                        <button
                          type="button"
                          className="text-slate-700 hover:underline"
                          onClick={() =>
                            setMovementsAccountId((cur) => (cur === a.id ? null : a.id))
                          }
                        >
                          Ver movimientos
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {showPanel && (
        <div className="bg-white border rounded-lg p-4 space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 className="font-medium text-gray-900">Editar banco y vínculos</h3>
              <p className="text-xs text-gray-500">
                Sucursal de referencia + Madres/Central que pueden enviar plata a esta cuenta.
              </p>
            </div>
            <select
              className="border rounded-md px-3 py-2 text-sm"
              value={selectedAccountId}
              onChange={(e) => {
                setSelectedAccountId(e.target.value);
                setSearchParams(
                  e.target.value
                    ? { account: e.target.value, edit: '1', link: '1' }
                    : {},
                );
              }}
            >
              <option value="">Cuenta bancaria…</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.bankName} · {a.alias || a.accountNumber || a.cbu?.slice(-4) || a.id.slice(0, 6)}
                </option>
              ))}
            </select>
          </div>

          {!selectedAccountId ? (
            <p className="text-sm text-gray-500">Elegí una cuenta.</p>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="block text-sm">
                  <span className="font-medium text-slate-800">Nombre del banco</span>
                  <input
                    className="mt-1 w-full border rounded-md px-3 py-2"
                    value={editForm.bankName}
                    onChange={(e) => setEditForm((f) => ({ ...f, bankName: e.target.value }))}
                    disabled={busy}
                  />
                </label>
                <label className="block text-sm">
                  <span className="font-medium text-slate-800">Alias</span>
                  <input
                    className="mt-1 w-full border rounded-md px-3 py-2"
                    value={editForm.alias}
                    onChange={(e) => setEditForm((f) => ({ ...f, alias: e.target.value }))}
                    disabled={busy}
                  />
                </label>
                <label className="block text-sm">
                  <span className="font-medium text-slate-800">Nº de cuenta</span>
                  <input
                    className="mt-1 w-full border rounded-md px-3 py-2"
                    value={editForm.accountNumber}
                    onChange={(e) => setEditForm((f) => ({ ...f, accountNumber: e.target.value }))}
                    disabled={busy}
                  />
                </label>
                <label className="block text-sm">
                  <span className="font-medium text-slate-800">CBU</span>
                  <input
                    className="mt-1 w-full border rounded-md px-3 py-2"
                    value={editForm.cbu}
                    onChange={(e) => setEditForm((f) => ({ ...f, cbu: e.target.value }))}
                    disabled={busy}
                  />
                </label>
                <label className="block text-sm">
                  <span className="font-medium text-slate-800">Sucursal de referencia</span>
                  <select
                    className="mt-1 w-full border rounded-md px-3 py-2"
                    value={editForm.branchId}
                    onChange={(e) => setEditForm((f) => ({ ...f, branchId: e.target.value }))}
                    disabled={busy}
                  >
                    <option value="">Empresa (sin sucursal fija)</option>
                    {branches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name || b.code}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block text-sm">
                  <span className="font-medium text-slate-800">Plantilla extracto</span>
                  <select
                    className="mt-1 w-full border rounded-md px-3 py-2"
                    value={editForm.templateCode}
                    onChange={(e) => setEditForm((f) => ({ ...f, templateCode: e.target.value }))}
                    disabled={busy}
                  >
                    {TEMPLATE_OPTIONS.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex items-center gap-2 text-sm md:col-span-2 pt-2">
                  <input
                    type="checkbox"
                    checked={editForm.isActive}
                    onChange={(e) => setEditForm((f) => ({ ...f, isActive: e.target.checked }))}
                    disabled={busy}
                  />
                  Cuenta activa
                </label>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void saveAccount()}
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md disabled:opacity-50"
                >
                  Guardar datos del banco
                </button>
              </div>

              <div className="border-t pt-4 space-y-3">
                <div>
                  <h4 className="font-medium text-gray-900">Vínculo ↔ Central / Madre</h4>
                  <p className="text-xs text-gray-500">
                    Marcá las Madres (por sucursal) y/o Central que pueden depositar o retirar hacia
                    este banco.
                  </p>
                </div>
                {linkableRegisters.length === 0 ? (
                  <p className="text-sm text-gray-500">No hay cajas Central/Madre en esta empresa.</p>
                ) : (
                  <div className="flex flex-wrap gap-3">
                    {linkableRegisters.map((r) => (
                      <label
                        key={r.id}
                        className="flex items-center gap-2 text-sm border rounded-md px-3 py-2"
                      >
                        <input
                          type="checkbox"
                          checked={selectedRegisterIds.includes(r.id)}
                          onChange={() => toggleRegister(r.id)}
                          disabled={busy}
                        />
                        {registerLabel(r)}
                      </label>
                    ))}
                  </div>
                )}
                <button
                  type="button"
                  disabled={busy || !selectedAccountId}
                  onClick={() => void saveLinks()}
                  className="px-3 py-1.5 text-sm bg-slate-900 text-white rounded-md disabled:opacity-50"
                >
                  Guardar vínculos Madres
                </button>
              </div>
            </>
          )}

          <button
            type="button"
            className="px-3 py-1.5 text-sm border rounded-md hover:bg-slate-50"
            onClick={() => setSearchParams(focusAccountId ? { account: focusAccountId } : {})}
          >
            Cerrar panel
          </button>
        </div>
      )}

      {movementsAccountId && (
        <div className="bg-white border rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b bg-slate-50 flex flex-wrap justify-between gap-2">
            <h3 className="font-medium text-gray-900">
              Movimientos caja ↔ banco ·{' '}
              {accounts.find((a) => a.id === movementsAccountId)?.bankName ?? movementsAccountId}
            </h3>
            <button
              type="button"
              className="text-sm text-slate-600 hover:underline"
              onClick={() => setMovementsAccountId(null)}
            >
              Cerrar
            </button>
          </div>
          {accountTransfers.length === 0 ? (
            <p className="px-4 py-4 text-sm text-gray-500">Sin depósitos/extracciones registrados.</p>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-white text-left text-gray-500">
                <tr>
                  <th className="px-3 py-2">Fecha</th>
                  <th className="px-3 py-2">Dirección</th>
                  <th className="px-3 py-2">Caja</th>
                  <th className="px-3 py-2">Monto</th>
                  <th className="px-3 py-2">Estado</th>
                </tr>
              </thead>
              <tbody>
                {accountTransfers.map((t) => (
                  <tr key={t.id} className="border-t">
                    <td className="px-3 py-2">
                      {formatDateTime(t.requestedAt || t.createdAt)}
                    </td>
                    <td className="px-3 py-2">
                      {t.direction === 'DEPOSIT' ? 'Depósito' : 'Extracción'}
                    </td>
                    <td className="px-3 py-2">{t.cashRegister?.code || t.cashRegisterId}</td>
                    <td className="px-3 py-2 tabular-nums">{formatMoney(Number(t.amount))}</td>
                    <td className="px-3 py-2">{transferStatusLabel(t.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="px-4 py-2 text-xs text-gray-500 border-t">
            Para registrar uno nuevo usá{' '}
            <Link to="/treasury/move?type=deposit" className="text-blue-700 hover:underline">
              Mover fondos
            </Link>
            .
          </p>
        </div>
      )}
    </div>
  );
}
